import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import { scaleOrdinal } from 'd3-scale';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { Loader } from '../../Common/Loader';
import { colorType5, logStyles } from './LogStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import GroupBarChartsComp from '../common/charts/barChart-builder-Grouped';
import LogKpiRow from './LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import { ISitee } from '../../ClientLanding';
import { useNav } from '../../../providers/NavProvider';

interface Props {
  siteId: string;
  logTitle: string;
  sitesResp: ISitee;
  projectId: string;
}

export default function NoiseLogScreen(props: Props) {
  const [noiseLogData, setNoiseLogData] = useState<null | any>(null);
  const [noiseLogColumns, setNoiseLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { sitesResp } = useNav();

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const logTitle = props.logTitle;
          const accessToken = await getAccessToken();
          const json = await getAllItems(logTitle, accessToken, clientAPIURL, props.siteId, props.projectId ? props.projectId : "", packageSite.id);
          setNoiseLogData(json.items);
          setNoiseLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return noiseLogData && noiseLogColumns
    ? <CreateNoiseLogCharts noiseLogData={noiseLogData} noiseLogColumns={noiseLogColumns} siteId={props.siteId} logTitle={props.logTitle} clientAPIURL={clientAPIURL} />
    : <Loader />;
}

interface CreateNoiseLogChartsProps {
  noiseLogData: any;
  noiseLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
}

const CreateNoiseLogCharts: React.FC<CreateNoiseLogChartsProps> = ({ noiseLogData, noiseLogColumns, siteId, logTitle, clientAPIURL }) => {
  const { getAccessToken } = useAuth();
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';

  const ColumnsWithIDColumn = noiseLogColumns && [
    ...noiseLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const [filteredData, setFilteredData] = useState<any[]>(noiseLogData ?? []);

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const newItem = { ...result.items[0] };
        setFilteredData(prev => {
          const without = prev.filter((oItem: any) => oItem.ID !== item.ID);
          return [...without, newItem];
        });
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  const chartData = useMemo(() => {
    const uniquePeriod = [...new Set(filteredData.map((item: any) => item.Period))] as string[];
    const dayNoise = filteredData.filter((a: any) => a.TimePeriod === 'Day');
    const eveNoise = filteredData.filter((a: any) => a.TimePeriod === 'Evening');
    const nightNoise = filteredData.filter((a: any) => a.TimePeriod === 'Night');

    const countByPeriod = (source: any[], conformity: string) =>
      uniquePeriod.map(p => source.filter((i: any) => i.Period === p && i.Conformity === conformity).length);

    const colorLine = scaleOrdinal(schemeCategory10);
    const makeDatasets = (conf: number[], nonConf: number[]) => [
      { data: conf, strokeWidth: 2, color: (opacity = 1) => colorLine('0') + Math.round(opacity * 255).toString(16) },
      { data: nonConf, strokeWidth: 2, color: (opacity = 1) => colorLine('1') + Math.round(opacity * 255).toString(16) },
    ];

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineData1 = { labels: uniquePeriod, datasets: makeDatasets(countByPeriod(filteredData, 'Conformity'), countByPeriod(filteredData, 'Non Conformity')) };
    const lineDataDayChart = { labels: uniquePeriod, datasets: makeDatasets(countByPeriod(dayNoise, 'Conformity'), countByPeriod(dayNoise, 'Non Conformity')) };
    const lineDataEveChart = { labels: uniquePeriod, datasets: makeDatasets(countByPeriod(eveNoise, 'Conformity'), countByPeriod(eveNoise, 'Non Conformity')) };
    const lineDataNightChart = { labels: uniquePeriod, datasets: makeDatasets(countByPeriod(nightNoise, 'Conformity'), countByPeriod(nightNoise, 'Non Conformity')) };

    const BarData1 = {
      labels: uniquePeriod,
      datasets: [{ data: countByPeriod(filteredData, 'Conformity'), strokeWidth: 2, color: (opacity = 1) => colorLine('0') + Math.round(opacity * 255).toString(16) }],
    };
    const BarData2 = {
      labels: uniquePeriod,
      datasets: [{ data: countByPeriod(filteredData, 'Non Conformity'), strokeWidth: 2, color: (opacity = 1) => colorLine('1') + Math.round(opacity * 255).toString(16) }],
    };

    // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
    const buildPie = (source: any[]) => {
      const acc: Record<string, number> = {};
      source.forEach((i: any) => { if (i.Conformity != null) acc[i.Conformity] = (acc[i.Conformity] ?? 0) + 1; });
      return Object.entries(acc).map(([name, value], idx) => ({ name, value, color: colorType5(idx.toString()) }));
    };

    return {
      uniquePeriod,
      lineData1, lineDataDayChart, lineDataEveChart, lineDataNightChart,
      BarData1, BarData2,
      byConformityPieData: buildPie(filteredData),
      byConformityPieDataDay: buildPie(dayNoise),
      byConformityPieDataEve: buildPie(eveNoise),
      byConformityPieDataNight: buildPie(nightNoise),
    };
  }, [filteredData]);

  const legendData1 = [{ name: 'Conformity' }, { name: 'Non Conformity' }];

  const exceedanceDateSet = new Set(
    filteredData.filter((i: any) => i.Conformity === 'Non Conformity' && i.Date).map((i: any) => i.Date)
  );
  const allDateSet = new Set(filteredData.filter((i: any) => i.Date).map((i: any) => i.Date));
  const exceedanceDays = exceedanceDateSet.size;
  const nonExceedanceDays = [...allDateSet].filter(d => !exceedanceDateSet.has(d)).length;
  const exceedanceEntries = filteredData.filter((i: any) => i.Conformity === 'Non Conformity').length;
  const nonExceedanceEntries = filteredData.filter((i: any) => i.Conformity === 'Conformity').length;

  const kpiItems = [
    { label: 'Total', value: allDateSet.size, unit: 'days', subtitle: `${filteredData.length} entries`, color: '#1565C0' },
    { label: 'Non Exceedances', value: nonExceedanceDays, unit: 'days', subtitle: `${nonExceedanceEntries} entries`, color: '#2E7D32' },
    { label: 'Exceedances', value: exceedanceDays, unit: 'days', subtitle: `${exceedanceEntries} entries`, color: '#C62828' },
  ];

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab && defaultTab === 'list' ? 1 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={{ zIndex: 200 }}>
                <LogKpiRow items={kpiItems}>
                  <LogDateRangeFilter data={noiseLogData} onFilterChange={setFilteredData} />
                </LogKpiRow>
              </View>
              <View>
                <View style={logStyles.container}>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <GroupBarChartsComp data={chartData.BarData1} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"Total Conformity"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <GroupBarChartsComp data={chartData.BarData2} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"Total Non Conformity"} />
                  </View>
                  {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.byConformityPieData} colorPallate={interpolateBlues} subTitle={'(Day/Eve/Night)'} ChartTitle={"Total"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={legendData1} data={chartData.lineData1} label={chartData.uniquePeriod} subTitle={'(Day/Eve/Night)'} ChartTitle={"Total"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.byConformityPieDataDay} colorPallate={interpolateBlues} subTitle={'(Day)'} ChartTitle={"Total"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.byConformityPieDataEve} colorPallate={interpolateBlues} subTitle={'(Eve)'} ChartTitle={"Total"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.byConformityPieDataNight} colorPallate={interpolateBlues} subTitle={'(Night)'} ChartTitle={"Total"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={legendData1} data={chartData.lineDataDayChart} label={chartData.uniquePeriod} subTitle={'(Day)'} ChartTitle={"Total"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={legendData1} data={chartData.lineDataEveChart} label={chartData.uniquePeriod} subTitle={'(Eve)'} ChartTitle={"Total"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={legendData1} data={chartData.lineDataNightChart} label={chartData.uniquePeriod} subTitle={'(Night)'} ChartTitle={"Total"} />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {filteredData && ColumnsWithIDColumn && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                        funcSetShowFilter={() => {}}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
